import { supabase } from '@/integrations/supabase/client'
import { smartleadAPI } from '@/lib/smartlead-api'

export interface ComprehensiveSyncResult {
  success: boolean
  updatedCampaigns: number
  updatedVariants: number
  errors: string[]
  details: {
    campaigns: {
      active: number
      completed: number
      paused: number
      drafted: number
      archived: number
    }
    variants: {
      active: number
      completed: number
      paused: number
      drafted: number
    }
  }
}

/**
 * Comprehensive sync service that syncs both campaigns and variants
 * with Smartlead API data
 */
export class ComprehensiveSyncService {
  
  /**
   * Sync both campaigns and variants with Smartlead API
   */
  static async syncAll(): Promise<ComprehensiveSyncResult> {
    console.log('🔄 Starting comprehensive sync with Smartlead API...')
    
    const result: ComprehensiveSyncResult = {
      success: true,
      updatedCampaigns: 0,
      updatedVariants: 0,
      errors: [],
      details: {
        campaigns: {
          active: 0,
          completed: 0,
          paused: 0,
          drafted: 0,
          archived: 0
        },
        variants: {
          active: 0,
          completed: 0,
          paused: 0,
          drafted: 0
        }
      }
    }

    try {
      // Step 1: Fetch Smartlead campaigns
      console.log('📊 Fetching Smartlead campaigns...')
      const smartleadCampaigns = await smartleadAPI.fetchCampaigns()
      console.log(`📊 Found ${smartleadCampaigns.length} Smartlead campaigns`)

      // Step 2: Sync variants first (they drive campaign status)
      console.log('🔄 Syncing variants with Smartlead API...')
      const variantResult = await this.syncVariants(smartleadCampaigns)
      result.updatedVariants = variantResult.updatedVariants
      result.details.variants = variantResult.details
      result.errors.push(...variantResult.errors)

      // Step 3: Sync campaigns based on variant statuses
      console.log('🔄 Syncing campaigns based on variant statuses...')
      const campaignResult = await this.syncCampaigns(smartleadCampaigns)
      result.updatedCampaigns = campaignResult.updatedCampaigns
      result.details.campaigns = campaignResult.details
      result.errors.push(...campaignResult.errors)

      result.success = result.errors.length === 0
      console.log(`🎉 Comprehensive sync completed: ${result.updatedCampaigns} campaigns, ${result.updatedVariants} variants updated`)

    } catch (error) {
      result.success = false
      const errorMsg = `Comprehensive sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      result.errors.push(errorMsg)
      console.error(`💥 ${errorMsg}`)
    }

    return result
  }

  /**
   * Sync variant statuses with Smartlead API
   */
  private static async syncVariants(smartleadCampaigns: any[]): Promise<{
    updatedVariants: number
    details: any
    errors: string[]
  }> {
    const result = {
      updatedVariants: 0,
      details: {
        active: 0,
        completed: 0,
        paused: 0,
        drafted: 0
      },
      errors: [] as string[]
    }

    try {
      // Get all variants with Smartlead campaign IDs
      const { data: variants, error: variantsError } = await supabase
        .from('ab_test_variants')
        .select('id, name, smartlead_campaign_id, status')
        .not('smartlead_campaign_id', 'is', null)

      if (variantsError) {
        throw new Error(`Failed to fetch variants: ${variantsError.message}`)
      }

      console.log(`📊 Found ${variants.length} variants with Smartlead IDs`)

      // Create a map of Smartlead campaign ID to status
      const smartleadStatusMap = new Map()
      smartleadCampaigns.forEach(campaign => {
        smartleadStatusMap.set(campaign.id?.toString(), campaign.status)
      })

      // Canonical status set is {active, paused, completed, draft, archived}
      // (enforced by ab_test_variants_status_check CHECK constraint). SmartLead
      // returns DRAFTED / STOPPED which don't belong; map them on the way in
      // so the sync job stops re-introducing non-canonical values.
      const canonicaliseStatus = (raw: string): string => {
        const s = raw.toLowerCase()
        if (s === 'drafted') return 'draft'
        if (s === 'stopped') return 'paused'
        return s
      }

      // Update variants that have status mismatches
      for (const variant of variants) {
        const smartleadStatus = smartleadStatusMap.get(variant.smartlead_campaign_id)

        if (smartleadStatus) {
          const canonical = canonicaliseStatus(smartleadStatus)
          if (canonical !== variant.status) {
            console.log(`🔄 Updating variant ${variant.name}: ${variant.status} → ${canonical} (from SmartLead ${smartleadStatus})`)

            const { error: updateError } = await supabase
              .from('ab_test_variants')
              .update({ status: canonical })
              .eq('id', variant.id)

            if (updateError) {
              result.errors.push(`Failed to update variant ${variant.name}: ${updateError.message}`)
            } else {
              result.updatedVariants++
              result.details[canonical as keyof typeof result.details]++
              console.log(`✅ Updated variant ${variant.name} to ${canonical}`)
            }
          }
        }
      }

    } catch (error) {
      const errorMsg = `Variant sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      result.errors.push(errorMsg)
      console.error(`💥 ${errorMsg}`)
    }

    return result
  }

  /**
   * Sync campaign statuses based on their variant statuses
   */
  private static async syncCampaigns(smartleadCampaigns: any[]): Promise<{
    updatedCampaigns: number
    details: any
    errors: string[]
  }> {
    const result = {
      updatedCampaigns: 0,
      details: {
        active: 0,
        completed: 0,
        paused: 0,
        drafted: 0,
        archived: 0
      },
      errors: [] as string[]
    }

    try {
      // Get all campaigns (excluding manually archived ones)
      const { data: campaigns, error: campaignsError } = await supabase
        .from('campaigns')
        .select('id, name, status, client_id')
        .neq('status', 'archived')
        .order('created_at', { ascending: false })

      if (campaignsError) {
        throw new Error(`Failed to fetch campaigns: ${campaignsError.message}`)
      }

      console.log(`📊 Found ${campaigns.length} campaigns to sync`)

      // Process each campaign
      for (const campaign of campaigns) {
        try {
          const newStatus = await this.determineCampaignStatusFromVariants(campaign.id, smartleadCampaigns)
          
          if (newStatus && newStatus !== campaign.status) {
            console.log(`🔄 Updating campaign ${campaign.name} (${campaign.id}): ${campaign.status} → ${newStatus}`)
            
            const { error: updateError } = await supabase
              .from('campaigns')
              .update({ status: newStatus })
              .eq('id', campaign.id)

            if (updateError) {
              result.errors.push(`Failed to update campaign ${campaign.name}: ${updateError.message}`)
            } else {
              result.updatedCampaigns++
              result.details[newStatus as keyof typeof result.details]++
              console.log(`✅ Updated campaign ${campaign.name} to ${newStatus}`)
            }
          } else {
            console.log(`📝 Campaign ${campaign.name} status unchanged (${campaign.status})`)
          }
        } catch (error) {
          const errorMsg = `Error processing campaign ${campaign.name}: ${error instanceof Error ? error.message : 'Unknown error'}`
          result.errors.push(errorMsg)
          console.error(`❌ ${errorMsg}`)
        }
      }

    } catch (error) {
      const errorMsg = `Campaign sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      result.errors.push(errorMsg)
      console.error(`💥 ${errorMsg}`)
    }

    return result
  }

  /**
   * Determine campaign status based on its variants' Smartlead statuses
   */
  private static async determineCampaignStatusFromVariants(
    campaignId: string, 
    smartleadCampaigns: any[]
  ): Promise<string | null> {
    
    // Get all segments for this campaign
    const { data: segments, error: segmentsError } = await supabase
      .from('ab_test_segments')
      .select('id')
      .eq('campaign_id', campaignId)

    if (segmentsError) {
      console.error(`Error fetching segments for campaign ${campaignId}:`, segmentsError)
      return null
    }

    if (!segments || segments.length === 0) {
      console.log(`📝 Campaign ${campaignId} has no segments, keeping current status`)
      return null
    }

    const segmentIds = segments.map(s => s.id)

    // Get all variants for these segments
    const { data: variants, error: variantsError } = await supabase
      .from('ab_test_variants')
      .select('smartlead_campaign_id')
      .in('segment_id', segmentIds)
      .not('smartlead_campaign_id', 'is', null)

    if (variantsError) {
      console.error(`Error fetching variants for campaign ${campaignId}:`, variantsError)
      return null
    }

    if (!variants || variants.length === 0) {
      console.log(`📝 Campaign ${campaignId} has no Smartlead variants, keeping current status`)
      return null
    }

    // Get Smartlead campaign statuses for these variants
    const smartleadCampaignIds = variants.map(v => v.smartlead_campaign_id)
    const relevantSmartleadCampaigns = smartleadCampaigns.filter(sc => 
      smartleadCampaignIds.includes(sc.id?.toString())
    )

    if (relevantSmartleadCampaigns.length === 0) {
      console.log(`📝 Campaign ${campaignId} has no matching Smartlead campaigns, keeping current status`)
      return null
    }

    // Determine status based on Smartlead campaign statuses
    const statuses = relevantSmartleadCampaigns.map(sc => sc.status)
    console.log(`🔍 Campaign ${campaignId} Smartlead statuses:`, statuses)

    // Normalize statuses to uppercase for comparison
    const normalizedStatuses = statuses.map(status => status.toUpperCase())
    
    // Priority logic:
    // 1. If ANY variant is ACTIVE → campaign = 'active'
    if (normalizedStatuses.includes('ACTIVE')) {
      return 'active'
    }

    // 2. If ALL variants are COMPLETED → campaign = 'completed'
    if (normalizedStatuses.every(status => status === 'COMPLETED')) {
      return 'completed'
    }

    // 3. If ALL variants are PAUSED → campaign = 'paused'
    if (normalizedStatuses.every(status => status === 'PAUSED')) {
      return 'paused'
    }

    // 4. If ALL variants are DRAFTED → campaign = 'draft'
    if (normalizedStatuses.every(status => status === 'DRAFTED')) {
      return 'draft'
    }

    // 5. Mixed statuses - use the most "active" status
    if (normalizedStatuses.includes('PAUSED')) {
      return 'paused'
    }
    if (normalizedStatuses.includes('COMPLETED')) {
      return 'completed'
    }
    if (normalizedStatuses.includes('DRAFTED')) {
      return 'draft'
    }

    console.log(`📝 Campaign ${campaignId} has mixed/unknown statuses, keeping current status`)
    return null
  }

  /**
   * Sync only variants (without affecting campaigns)
   */
  static async syncVariantsOnly(): Promise<{
    success: boolean
    updatedVariants: number
    errors: string[]
  }> {
    console.log('🔄 Starting variant-only sync with Smartlead API...')
    
    try {
      const smartleadCampaigns = await smartleadAPI.fetchCampaigns()
      const result = await this.syncVariants(smartleadCampaigns)
      
      return {
        success: result.errors.length === 0,
        updatedVariants: result.updatedVariants,
        errors: result.errors
      }
    } catch (error) {
      return {
        success: false,
        updatedVariants: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error']
      }
    }
  }

  /**
   * Sync only campaigns (without affecting variants)
   */
  static async syncCampaignsOnly(): Promise<{
    success: boolean
    updatedCampaigns: number
    errors: string[]
  }> {
    console.log('🔄 Starting campaign-only sync with Smartlead API...')
    
    try {
      const smartleadCampaigns = await smartleadAPI.fetchCampaigns()
      const result = await this.syncCampaigns(smartleadCampaigns)
      
      return {
        success: result.errors.length === 0,
        updatedCampaigns: result.updatedCampaigns,
        errors: result.errors
      }
    } catch (error) {
      return {
        success: false,
        updatedCampaigns: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error']
      }
    }
  }
}
